import { Link, useLocation } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { useAuth } from '../store/AuthContext'
import { APP_VERSION } from '../version'
import scoresService from '../services/scoresService'
import employeesService from '../services/employeesService'

interface MenuItem {
  path: string
  label: string
  icon: string
  roles?: ('admin' | 'manager' | 'viewer')[]
  badge?: 'pending-count'
  requiresEligible?: boolean
}

const menuItems: MenuItem[] = [
  {
    path: '/',
    label: 'Dashboard',
    icon: '\u{1F4CA}',
  },
  {
    path: '/minha-avaliacao',
    label: 'Minha Avaliação',
    icon: '\u{1F4DD}',
    roles: ['viewer'],
    requiresEligible: true,
  },
  {
    path: '/avaliar',
    label: 'Avaliar',
    icon: '\u2713',
    roles: ['admin', 'manager'],
  },
  {
    path: '/aprovacoes',
    label: 'Aprovações',
    icon: '\u{1F4E5}',
    roles: ['admin'],
    badge: 'pending-count',
  },
  {
    path: '/ranking',
    label: 'Ranking',
    icon: '\u{1F3C6}',
  },
  {
    path: '/historico',
    label: 'Histórico',
    icon: '\u{1F4C8}',
  },
  {
    path: '/minha-empresa',
    label: 'Minha Empresa',
    icon: '\u{1F3E2}',
  },
  {
    path: '/funcionarios',
    label: 'Funcionários',
    icon: '\u{1F465}',
    roles: ['admin', 'manager'],
  },
  {
    path: '/empresas',
    label: 'Empresas',
    icon: '\u{1F3E2}',
    roles: ['admin'],
  },
  {
    path: '/usuarios',
    label: 'Usuarios',
    icon: '\u{1F464}',
    roles: ['admin'],
  },
  {
    path: '/grupos',
    label: 'Grupos',
    icon: '\u{1F510}',
    roles: ['admin'],
  },
  {
    path: '/ciclagem',
    label: 'Ciclagem',
    icon: '\u{1F504}',
    roles: ['admin'],
  },
  {
    path: '/auditoria',
    label: 'Auditoria',
    icon: '\u{1F4CB}',
    roles: ['admin'],
  },
]

interface SidebarProps {
  collapsed: boolean
  onToggle: () => void
}

export const Sidebar = ({ collapsed, onToggle }: SidebarProps) => {
  const location = useLocation()
  const { user, theme } = useAuth()

  const logoSrc = theme === 'dark' ? '/logo-jonnpo-dark.png' : '/logo-jonnpo.png'

  // Pending count for admin badge
  const { data: pendingCount } = useQuery({
    queryKey: ['pending-count'],
    queryFn: () => scoresService.getPendingCount(),
    enabled: user?.role === 'admin',
    refetchInterval: 60000,
  })

  // Check if viewer is eligible (has employee profile + is_ownership_eligible)
  const { data: assessmentStatus } = useQuery({
    queryKey: ['my-assessment-status'],
    queryFn: () => scoresService.getMyAssessmentStatus(),
    enabled: user?.role === 'viewer',
  })

  // Get employee's primary company for "Minha Empresa" link
  const { data: allEmployees = [] } = useQuery({
    queryKey: ['employees'],
    queryFn: () => employeesService.getAll(),
    staleTime: 120_000,
  })
  const myEmployee = allEmployees.find((e) => e.user_detail?.id === user?.id)
  const myCompanyId = myEmployee?.primary_company_detail?.id

  const canAccessItem = (item: MenuItem) => {
    if (item.path === '/minha-empresa') return !!myCompanyId
    if (!item.roles) return true
    if (!user || !item.roles.includes(user.role)) return false
    if (item.requiresEligible && user.role === 'viewer') {
      return (
        assessmentStatus?.has_employee &&
        assessmentStatus?.is_eligible &&
        assessmentStatus?.self_assessment_enabled
      )
    }
    return true
  }

  const getItemPath = (item: MenuItem) => {
    if (item.path === '/minha-empresa' && myCompanyId) {
      return `/empresas/${myCompanyId}/dashboard`
    }
    return item.path
  }

  const isActive = (path: string) => {
    if (path === '/minha-empresa' && myCompanyId) {
      return location.pathname === `/empresas/${myCompanyId}/dashboard`
    }
    return location.pathname === path
  }

  const getBadgeCount = (item: MenuItem): number | null => {
    if (item.badge === 'pending-count' && pendingCount?.count) {
      return pendingCount.count
    }
    return null
  }

  return (
    <aside
      className={`fixed left-0 top-0 h-screen bg-white dark:bg-gray-900 border-r border-gray-200 dark:border-gray-800 flex flex-col shadow-lg transition-all duration-300 z-20 ${
        collapsed ? 'w-20' : 'w-64'
      }`}
    >
      {/* Logo + Toggle */}
      <div className="border-b border-gray-200 dark:border-gray-800">
        <div className={`flex items-center ${collapsed ? 'justify-center p-4' : 'p-5'}`}>
          {collapsed ? (
            <img
              src={logoSrc}
              alt="Jonnpo"
              className="w-10 h-10 object-contain"
            />
          ) : (
            <div className="flex flex-col items-center flex-1">
              <img
                src={logoSrc}
                alt="Jonnpo"
                className="h-10 object-contain"
              />
              <h1 className="text-sm font-bold text-gray-900 dark:text-white mt-1">
                Ownership
              </h1>
            </div>
          )}
        </div>

        {/* Toggle Button */}
        <button
          onClick={onToggle}
          className={`w-full flex items-center justify-center py-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors ${
            collapsed ? '' : 'border-t border-gray-100 dark:border-gray-800'
          }`}
          title={collapsed ? 'Expandir menu' : 'Recolher menu'}
        >
          <svg
            className={`w-5 h-5 transition-transform duration-300 ${collapsed ? 'rotate-180' : ''}`}
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 19l-7-7 7-7" />
          </svg>
        </button>
      </div>

      {/* Menu Items */}
      <nav className="flex-1 overflow-y-auto py-3">
        {menuItems
          .filter(canAccessItem)
          .map((item) => {
            const badgeCount = getBadgeCount(item)
            return (
              <Link
                key={item.path}
                to={getItemPath(item)}
                title={collapsed ? item.label : undefined}
                className={`
                  group relative flex items-center transition-colors
                  ${collapsed ? 'justify-center px-2 py-3 mx-2 rounded-lg' : 'gap-3 px-6 py-3'}
                  ${
                    isActive(item.path)
                      ? collapsed
                        ? 'bg-primary/10 dark:bg-primary/20 text-primary dark:text-primary font-medium'
                        : 'bg-primary/10 dark:bg-primary/20 text-primary dark:text-primary border-l-4 border-primary font-medium'
                      : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800'
                  }
                `}
              >
                <span className="text-xl relative">
                  {item.icon}
                  {badgeCount !== null && collapsed && (
                    <span className="absolute -top-2 -right-2 min-w-[18px] h-[18px] flex items-center justify-center bg-danger text-white text-[10px] font-bold rounded-full px-1">
                      {badgeCount > 99 ? '99+' : badgeCount}
                    </span>
                  )}
                </span>
                {!collapsed && (
                  <span className="font-medium flex-1 flex items-center justify-between">
                    {item.label}
                    {badgeCount !== null && (
                      <span className="ml-2 min-w-[20px] h-[20px] flex items-center justify-center bg-danger text-white text-xs font-bold rounded-full px-1.5">
                        {badgeCount > 99 ? '99+' : badgeCount}
                      </span>
                    )}
                  </span>
                )}

                {/* Tooltip when collapsed */}
                {collapsed && (
                  <span className="absolute left-full ml-2 px-2 py-1 bg-gray-900 dark:bg-gray-700 text-white text-xs rounded whitespace-nowrap opacity-0 group-hover:opacity-100 pointer-events-none transition-opacity z-50">
                    {item.label}
                    {badgeCount !== null && ` (${badgeCount})`}
                  </span>
                )}
              </Link>
            )
          })}
      </nav>

      {/* Footer - Version */}
      <div className={`border-t border-gray-200 dark:border-gray-800 text-xs text-gray-500 dark:text-gray-400 ${
        collapsed ? 'p-2 text-center' : 'p-4'
      }`}>
        {collapsed ? `v${APP_VERSION.split('.')[0]}` : `v${APP_VERSION}`}
      </div>
    </aside>
  )
}

export default Sidebar
